/**
 * WEB-597 — canonical share-payload builder (U1).
 *
 * Every share surface must route through this so URLs stay on the marketing
 * city host via `cityUrl` (R2) and text is never fabricated (R6).
 */

import { cityUrl, defaultCityCode } from '@/lib/cross-host-urls';

import type { ShareEntityDescriptor, ShareEntityType, SharePayload } from './types';

/** Path segment for each entity type — `/<type>/<slug>` on the city host. */
const PATH_BY_TYPE: Record<ShareEntityType, string> = {
  event: 'event',
  venue: 'venue',
  artist: 'artist',
  city: 'city',
};

/**
 * Build share text from present descriptor fields only.
 *
 * Per-type today: use the explicit `text` when provided. Missing / blank text
 * is omitted — no `"Check out ${title}"` (or any other) fabricated copy (R6).
 */
function buildShareText(descriptor: ShareEntityDescriptor): string | undefined {
  switch (descriptor.type) {
    case 'event':
    case 'venue':
    case 'artist':
    case 'city': {
      const trimmed = descriptor.text?.trim();
      return trimmed ? trimmed : undefined;
    }
    default: {
      const _exhaustive: never = descriptor;
      void _exhaustive;
      return undefined;
    }
  }
}

/**
 * Derive `{ title, text?, url }` from an entity descriptor.
 *
 * `url` is always `cityUrl(cityCode ?? defaultCityCode(), '/<type>/<slug>')`
 * — never a live-origin / app-host URL (R2).
 */
export function buildSharePayload(descriptor: ShareEntityDescriptor): SharePayload {
  const cityCode = descriptor.cityCode ?? defaultCityCode();
  const pathSegment = PATH_BY_TYPE[descriptor.type];
  const url = cityUrl(cityCode, `/${pathSegment}/${encodeURIComponent(descriptor.slug)}`);
  const text = buildShareText(descriptor);

  return {
    title: descriptor.title,
    ...(text !== undefined ? { text } : {}),
    url,
  };
}
